import { InfiniteScrollApi } from "./infinite-scroll-api";
import type { InfiniteScrollForm } from "./infinite-scroll-form";
import type { InfiniteScrollRender } from "./infinite-scroll-render";
import { InfiniteScrollUtils } from "./infinite-scroll-utils";

export class InfiniteScrollHandler {
    public form: InfiniteScrollForm;
    public render: InfiniteScrollRender;
    public isLoading: boolean;
    public isDone: boolean;

    public constructor(form: InfiniteScrollForm, render: InfiniteScrollRender) {
        this.form = form;
        this.render = render;
        this.isLoading = false;
        this.isDone = false;
    }

    private finish(): void {
        this.isDone = true;
        this.render.hideSentinel();

        const observer = this.form.getObserver();
        if (observer instanceof IntersectionObserver) {
            observer.disconnect();
            this.form.setObserver(null);
        }
    }

    public bootstrap(): void {
        if (!(this.form.getListElement() instanceof HTMLElement)) return;
        if (!(this.form.getPaginationElement() instanceof HTMLElement)) return;

        const nextUrl = InfiniteScrollUtils.findNextUrl(
            document,
            this.form.getPaginationSelector(),
            this.form.getNextLinkSelector(),
        );

        this.form.setNextUrl(nextUrl);
        if (!nextUrl) {
            this.finish();
            return;
        }

        this.render.ensureSentinel();
        this.render.setIndicatorVisible(false);
        this.render.setPaginationHidden(false);
    }

    public setEnabled(enabled: boolean): void {
        if (!enabled) {
            this.render.setIndicatorVisible(false);
            this.render.hideSentinel();
            this.render.setPaginationHidden(false);
            return;
        }

        const nextUrl = this.form.getNextUrl();
        if (!nextUrl || this.isDone) {
            this.render.hideSentinel();
            if (this.form.shouldHidePaginationOnMobile()) {
                this.render.setPaginationHidden(true);
            }
            return;
        }

        this.render.showSentinel();

        if (this.form.shouldHidePaginationOnMobile()) {
            this.render.setPaginationHidden(true);
        }
    }

    public async handleIntersect(entries: IntersectionObserverEntry[]): Promise<void> {
        if (!Array.isArray(entries)) return;
        const hit = entries.some((entry) => entry && entry.isIntersecting);
        if (!hit) return;
        await this.loadNextPage();
    }

    public async loadNextPage(): Promise<void> {
        if (this.isLoading || this.isDone) return;

        const nextUrl = this.form.getNextUrl();
        if (!nextUrl) {
            this.finish();
            return;
        }

        this.isLoading = true;
        this.render.setIndicatorVisible(true);

        try {
            const doc = await InfiniteScrollApi.fetchPageDocument(nextUrl);

            const appendedCount = this.render.appendItemsFromDocument(doc);
            if (appendedCount <= 0) {
                this.finish();
                return;
            }

            const appendExtras = this.form.getAppendExtras();
            if (appendExtras) {
                appendExtras(doc, document);
            }

            const upcomingUrl = InfiniteScrollUtils.findNextUrl(
                doc,
                this.form.getPaginationSelector(),
                this.form.getNextLinkSelector(),
            );
            this.form.setNextUrl(upcomingUrl);

            if (!upcomingUrl) {
                this.finish();
            }

            this.render.dispatchContentInserted();
        } catch (error) {
            console.error("[InfiniteScroll] load failed", error);
            this.finish();
        } finally {
            this.isLoading = false;
            this.render.setIndicatorVisible(false);
        }
    }
}
